import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './OrdiniPage.css';

// Importa la configurazione API
// In produzione usa percorso relativo (shop è in /shop, backend in /shop/backend)
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/shop/backend/api' 
  : 'http://efwypvu.cluster021.hosting.ovh.net/shop/backend/api';

const OrdiniPage = () => {
  const navigate = useNavigate();
  const publicUrl = process.env.PUBLIC_URL || '';
  const [ordini, setOrdini] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedOrdine, setSelectedOrdine] = useState(null);

  // Filtri e ordinamento
  const [filteredOrdini, setFilteredOrdini] = useState([]);
  const [filters, setFilters] = useState({
    searchText: '',
    sortBy: 'data_ordine', // 'data_ordine', 'totale', 'nome_cliente', 'pagato'
    sortOrder: 'desc', // 'asc', 'desc'
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    checkAuth();
    loadOrdini();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [ordini, filters]);

  const checkAuth = () => {
    console.log('🔍 Verifica autenticazione locale...');
    
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    const adminData = localStorage.getItem('admin_data');
    
    if (!isLoggedIn || !adminData) {
      console.log('❌ Non autenticato, reindirizzamento al login...');
      navigate('/amministrazione');
      return;
    }
    
    console.log('✅ Autenticazione verificata!');
    setLoading(false);
  };

  const loadOrdini = async () => {
    try {
      console.log('📦 Caricamento ordini...');
      
      const response = await fetch(`${API_BASE_URL}/ordini.php`);
      const data = await response.json();
      
      console.log('📋 Ordini caricati:', data);
      
      if (data.success) {
        setOrdini(data.data || []);
      } else {
        console.error('❌ Errore caricamento ordini:', data.error);
      }
    } catch (err) {
      console.error('💥 Errore caricamento ordini:', err);
    }
  };

  const loadOrdineDetails = async (ordineId) => {
    try {
      console.log('🔍 Caricamento dettagli ordine:', ordineId);
      
      const response = await fetch(`${API_BASE_URL}/ordini.php?id=${ordineId}`);
      const data = await response.json();
      
      console.log('📋 Dettagli ordine caricati:', data);
      
      if (data.success) {
        return data.data;
      } else {
        console.error('❌ Errore caricamento dettagli ordine:', data.error);
        return null;
      }
    } catch (err) {
      console.error('💥 Errore caricamento dettagli ordine:', err);
      return null;
    }
  };

  const handleViewDetails = async (ordine) => {
    console.log('👁️ Visualizzazione dettagli ordine:', ordine.id_ordine);
    
    // Carica i dettagli completi dell'ordine
    const ordineDetails = await loadOrdineDetails(ordine.id_ordine);
    
    if (ordineDetails) {
      setSelectedOrdine(ordineDetails);
      setShowDetailsModal(true);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Funzioni per i filtri
  const applyFilters = () => {
    let filtered = [...ordini];

    // Filtro per testo di ricerca (nome, città, provincia, email, prodotti)
    if (filters.searchText.trim()) {
      const searchTerm = filters.searchText.toLowerCase();
      filtered = filtered.filter(ordine => {
        const nomeCliente = ordine.nome_cliente?.toLowerCase() || '';
        const citta = ordine.citta_cliente?.toLowerCase() || '';
        const provincia = ordine.provincia_cliente?.toLowerCase() || '';
        const email = ordine.email_cliente?.toLowerCase() || '';
        
        // Controlla anche nei prodotti dell'ordine
        const hasMatchingProduct = ordine.prodotti?.some(prodotto => 
          prodotto.nome_prodotto?.toLowerCase().includes(searchTerm)
        ) || false;
        
        return nomeCliente.includes(searchTerm) || 
               citta.includes(searchTerm) || 
               provincia.includes(searchTerm) || 
               email.includes(searchTerm) ||
               hasMatchingProduct;
      });
    }

    // Filtro per date
    if (filters.dateFrom) {
      filtered = filtered.filter(ordine => {
        const ordineDate = new Date(ordine.data_ordine);
        const fromDate = new Date(filters.dateFrom);
        return ordineDate >= fromDate;
      });
    }

    if (filters.dateTo) {
      filtered = filtered.filter(ordine => {
        const ordineDate = new Date(ordine.data_ordine);
        const toDate = new Date(filters.dateTo);
        // Aggiungi un giorno per includere l'intera giornata
        toDate.setDate(toDate.getDate() + 1);
        return ordineDate < toDate;
      });
    }

    // Ordinamento
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (filters.sortBy) {
        case 'nome_cliente':
          aValue = a.nome_cliente?.toLowerCase() || '';
          bValue = b.nome_cliente?.toLowerCase() || '';
          break;
        case 'totale':
          aValue = parseFloat(a.totale) || 0;
          bValue = parseFloat(b.totale) || 0;
          break;
        case 'data_ordine':
          aValue = new Date(a.data_ordine) || new Date(0);
          bValue = new Date(b.data_ordine) || new Date(0);
          break;
        case 'pagato':
          aValue = Number(a.pagato) === 1 ? 1 : 0;
          bValue = Number(b.pagato) === 1 ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (filters.sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredOrdini(filtered);
  };

  const handleSearchChange = (e) => {
    setFilters(prev => ({
      ...prev,
      searchText: e.target.value
    }));
  };

  const handleSortChange = (e) => {
    const [sortBy, sortOrder] = e.target.value.split('-');
    setFilters(prev => ({
      ...prev,
      sortBy: sortBy,
      sortOrder: sortOrder
    }));
  };

  const handleDateFromChange = (e) => {
    setFilters(prev => ({
      ...prev,
      dateFrom: e.target.value
    }));
  };

  const handleDateToChange = (e) => {
    setFilters(prev => ({
      ...prev,
      dateTo: e.target.value
    }));
  };

  const resetFilters = () => {
    setFilters({
      searchText: '',
      sortBy: 'data_ordine',
      sortOrder: 'desc',
      dateFrom: '',
      dateTo: ''
    });
  };

  const formatPrice = (price) => {
    return `€${parseFloat(price).toFixed(2)}`;
  };

  const getStatusBadge = (pagato) => {
    const isPaid = Number(pagato) === 1;
    if (isPaid) {
      return <span className="status-badge paid">Pagato</span>;
    } else {
      return <span className="status-badge unpaid">Non Pagato</span>;
    }
  };

  const getSpeditoBadge = (spedito) => {
    const isShipped = Number(spedito) === 1;
    if (isShipped) {
      return <span className="status-badge shipped">Spedito</span>;
    } else {
      return <span className="status-badge not-shipped">Non Spedito</span>;
    }
  };

  const handleUpdateSpedito = async (ordineId, newSpeditoValue) => {
    try {
      console.log('📦 Aggiornamento stato spedizione:', ordineId, newSpeditoValue);
      
      const response = await fetch(`${API_BASE_URL}/ordini.php`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          id_ordine: ordineId,
          spedito: newSpeditoValue ? 1 : 0
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log('✅ Stato spedizione aggiornato:', data);
        // Aggiorna l'ordine nella lista
        setOrdini(prevOrdini => 
          prevOrdini.map(ordine => 
            ordine.id_ordine === ordineId 
              ? { ...ordine, spedito: newSpeditoValue ? 1 : 0 }
              : ordine
          )
        );
        // Aggiorna anche l'ordine selezionato nel modal
        if (selectedOrdine && selectedOrdine.id_ordine === ordineId) {
          setSelectedOrdine(prev => ({ ...prev, spedito: newSpeditoValue ? 1 : 0 }));
        }
      } else {
        console.error('❌ Errore aggiornamento stato spedizione:', data.error);
        alert('Errore durante l\'aggiornamento dello stato di spedizione: ' + (data.error || 'Errore sconosciuto'));
      }
    } catch (err) {
      console.error('💥 Errore aggiornamento stato spedizione:', err);
      alert('Errore durante l\'aggiornamento dello stato di spedizione');
    }
  };

  if (loading) {
    return (
      <div className="ordini-page">
        <div className="loading">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="ordini-page admin-page">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <img src={`${publicUrl}/logomuseo.png`} alt="Museo Stibbert" className="sidebar-logo" />
        </div>

        <div className="sidebar-admin-title">
          <h1><strong style={{color: 'var(--bianco), font-weight: 700, font-size: 1.2rem'}}>Museo Stibbert</strong> Amministrazione</h1>
        </div>

        <nav className="sidebar-nav">
          <button className="nav-item" onClick={() => navigate('/amministrazione/dashboard')}>
            <i className="fas fa-tachometer-alt"></i>
            <span>Dashboard</span>
          </button>
          <button className="nav-item" onClick={() => navigate('/amministrazione/categorie')}>
            <i className="fas fa-tags"></i>
            <span>Categorie</span>
          </button>
          <button className="nav-item" onClick={() => navigate('/amministrazione/prodotti')}>
            <i className="fas fa-box"></i>
            <span>Prodotti</span>
          </button>
          <button className="nav-item active">
            <i className="fas fa-shopping-cart"></i>
            <span>Ordini</span>
          </button>
          <button className="nav-item" onClick={() => navigate('/amministrazione/impostazioni')}>
            <i className="fas fa-cog"></i>
            <span>Impostazioni</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <button className="nav-item logout-item" onClick={() => {
            localStorage.removeItem('admin_logged_in');
            localStorage.removeItem('admin_data');
            navigate('/amministrazione');
          }}>
            <i className="fas fa-sign-out-alt"></i>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <div className="page-header">
          <div className="header-content">
            <h1>Ordini</h1>
            <div className="header-actions">
            </div>
          </div>
        </div>

        <div className="main-content">
          {/* Filtri */}
          <div className="filters-container">
            {/* Prima riga: Date Range */}
            <div className="date-bar">
              <div className="date-group">
                <div className="date-input-group">
                  <label htmlFor="date-from">Da:</label>
                  <input
                    id="date-from"
                    type="date"
                    value={filters.dateFrom}
                    onChange={handleDateFromChange}
                    className="date-input"
                  />
                </div>
                <div className="date-input-group">
                  <label htmlFor="date-to">A:</label>
                  <input
                    id="date-to"
                    type="date"
                    value={filters.dateTo}
                    onChange={handleDateToChange}
                    className="date-input"
                  />
                </div>
              </div>
            </div>

            {/* Seconda riga: Ricerca e Controlli */}
            <div className="filters-bar">
              {/* Ricerca per testo - Sinistra */}
              <div className="search-group">
                <input
                  id="search-input"
                  type="text"
                  placeholder="Cerca per nome, città, provincia, email, prodotto..."
                  value={filters.searchText}
                  onChange={handleSearchChange}
                  className="search-input"
                />
              </div>

              {/* Ordinamento e Reset - Destra */}
              <div className="controls-group">
                <div className="sort-group">
                  <label htmlFor="sort-select">Ordina per:</label>
                  <select
                    id="sort-select"
                    className="sort-select"
                    value={`${filters.sortBy}-${filters.sortOrder}`}
                    onChange={handleSortChange}
                  >
                    <option value="data_ordine-desc">Data (Più Recenti)</option>
                    <option value="data_ordine-asc">Data (Più Vecchi)</option>
                    <option value="totale-asc">Totale (Crescente)</option>
                    <option value="totale-desc">Totale (Decrescente)</option>
                    <option value="nome_cliente-asc">Cliente (A-Z)</option>
                    <option value="nome_cliente-desc">Cliente (Z-A)</option>
                    <option value="pagato-desc">Pagato (Prima)</option>
                    <option value="pagato-asc">Non Pagato (Prima)</option>
                  </select>
                </div>
                <button className="reset-filters-btn" onClick={resetFilters}>
                  Reset Filtri
                </button>
              </div>
            </div>
          </div>

          <div className="ordini-table-container">
            {filteredOrdini.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-shopping-cart"></i>
                <h3>Nessun ordine presente</h3>
                <p>Gli ordini appariranno qui quando i clienti effettueranno acquisti</p>
              </div>
            ) : (
              <table className="ordini-table">
                <thead>
                  <tr>
                    <th>ID Ordine</th>
                    <th>Cliente</th>
                    <th>Totale</th>
                    <th>Stato</th>
                    <th>Spedito</th>
                    <th>Data</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrdini.map((ordine) => (
                    <tr key={ordine.id_ordine}>
                      <td className="ordine-id">
                        <strong>#{ordine.id_ordine}</strong>
                      </td>
                      <td className="ordine-cliente">
                        <div className="cliente-info">
                          <strong>{ordine.nome_cliente}</strong>
                        </div>
                      </td>
                      <td className="ordine-totale">
                        <strong>{formatPrice(ordine.totale)}</strong>
                      </td>
                      <td className="ordine-stato">
                        {getStatusBadge(ordine.pagato)}
                      </td>
                      <td className="ordine-spedito">
                        {getSpeditoBadge(ordine.spedito ?? 0)}
                      </td>
                      <td className="ordine-data">
                        {formatDate(ordine.data_ordine)}
                      </td>
                      <td className="ordine-actions">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleViewDetails(ordine)}
                          title="Visualizza Dettagli"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>

      {/* Modal Dettagli Ordine */}
      {showDetailsModal && selectedOrdine && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-ordini-dettagli" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Dettagli Ordine #{selectedOrdine.id_ordine}</h2>
              <button 
                className="modal-close"
                onClick={() => setShowDetailsModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="modal-body">
              {/* Layout a Griglia Moderno */}
              <div className="modal-grid">
                
                {/* Informazioni Cliente - Card Sinistra */}
                <div className="info-card client-card">
                  <div className="card-header">
                    <div className="card-icon">
                      <i className="fas fa-user"></i>
                    </div>
                    <h4>Cliente</h4>
                  </div>
                  <div className="card-content">
                    <div className="info-row">
                      <div className="info-label">Nome</div>
                      <div className="info-value">{selectedOrdine.nome_cliente}</div>
                    </div>
                    <div className="info-row">
                      <div className="info-label">Email</div>
                      <div className="info-value email-value">{selectedOrdine.email_cliente}</div>
                    </div>
                    <div className="info-row">
                      <div className="info-label">Telefono</div>
                      <div className="info-value">{selectedOrdine.telefono_cliente}</div>
                    </div>
                  </div>
                </div>

                {/* Indirizzo - Card Separata */}
                <div className="info-card address-card">
                  <div className="card-header">
                    <div className="card-icon">
                      <i className="fas fa-map-marker-alt"></i>
                    </div>
                    <h4>Spedizione</h4>
                  </div>
                  <div className="card-content">
                    <div className="info-row">
                      <div className="info-label">Via</div>
                      <div className="info-value">{selectedOrdine.indirizzo_cliente}</div>
                    </div>
                    <div className="info-row">
                      <div className="info-label">Civico</div>
                      <div className="info-value">{selectedOrdine.civico_cliente}</div>
                    </div>
                    <div className="info-row">
                      <div className="info-label">CAP</div>
                      <div className="info-value">{selectedOrdine.cap_cliente}</div>
                    </div>
                    <div className="info-row">
                      <div className="info-label">Città</div>
                      <div className="info-value">{selectedOrdine.citta_cliente}</div>
                    </div>
                    <div className="info-row">
                      <div className="info-label">Provincia</div>
                      <div className="info-value">{selectedOrdine.provincia_cliente}</div>
                    </div>
                    <div className="info-row">
                      <div className="info-label">Stato</div>
                      <div className="info-value">{selectedOrdine.stato_cliente}</div>
                    </div>
                  </div>
                </div>

                {/* Prodotti - Card Larga */}
                <div className="info-card products-card">
                  <div className="card-header">
                    <div className="card-icon">
                      <i className="fas fa-box"></i>
                    </div>
                    <h4>Prodotti Ordinati</h4>
                  </div>
                  <div className="card-content">
                    <div className="products-list">
                      {selectedOrdine.dettagli && selectedOrdine.dettagli.map((dettaglio, index) => (
                        <div key={index} className="product-detail">
                          <div className="info-row">
                            <div className="info-label">Prodotto</div>
                            <div className="info-value product-name">{dettaglio.nome_prodotto}</div>
                          </div>
                          <div className="info-row">
                            <div className="info-label">ID Prodotto</div>
                            <div className="info-value">#{dettaglio.id_prodotto}</div>
                          </div>
                          <div className="info-row">
                            <div className="info-label">Quantità</div>
                            <div className="info-value">{dettaglio.quantita}</div>
                          </div>
                          <div className="info-row">
                            <div className="info-label">Prezzo Unitario</div>
                            <div className="info-value">{formatPrice(dettaglio.prezzo_unitario)}</div>
                          </div>
                          <div className="info-row total-row">
                            <div className="info-label">Subtotale</div>
                            <div className="info-value total-price">{formatPrice(dettaglio.prezzo_unitario * dettaglio.quantita)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Riepilogo - Card Larga */}
                <div className="info-card summary-card">
                  <div className="card-header">
                    <div className="card-icon">
                      <i className="fas fa-receipt"></i>
                    </div>
                    <h4>Riepilogo Ordine</h4>
                  </div>
                  <div className="card-content">
                    <div className="summary-list">
                      <div className="info-row">
                        <div className="info-label">Data Ordine</div>
                        <div className="info-value">{formatDate(selectedOrdine.data_ordine)}</div>
                      </div>
                      <div className="info-row">
                        <div className="info-label">Metodo Pagamento</div>
                        <div className="info-value">Nexi</div>
                      </div>
                      <div className="info-row">
                        <div className="info-label">Stato Pagamento</div>
                        <div className="info-value">{getStatusBadge(selectedOrdine.pagato)}</div>
                      </div>
                      <div className="info-row">
                        <div className="info-label">Stato Spedizione</div>
                        <div className="info-value">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', justifyContent: 'flex-end' }}>
                            {getSpeditoBadge(selectedOrdine.spedito ?? 0)}
                            <label className="shipping-toggle" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={Number(selectedOrdine.spedito ?? 0) === 1}
                                onChange={(e) => handleUpdateSpedito(selectedOrdine.id_ordine, e.target.checked)}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                              />
                              <span style={{ fontSize: '0.9rem', color: 'var(--grigio-scuro)' }}>
                                {Number(selectedOrdine.spedito ?? 0) === 1 ? 'Segna come non spedito' : 'Segna come spedito'}
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="summary-total">
                        <div className="total-label">TOTALE ORDINE</div>
                        <div className="total-value">{formatPrice(selectedOrdine.totale)}</div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdiniPage;
